import { useEffect, useState } from 'react';
import { useSyncStatus } from '../auth/AuthProvider';
import { loadImage } from '../local-db/files';
import { useWorkspace } from './workspace';

/**
 * Pictures kept in this computer's database, as addresses an <img> can show. A picture that was
 * added on another computer arrives with the next sync, so this looks again after each one.
 * Returns an entry per id: the address, or null while it has not arrived.
 */
export function useFileUrls(ids: readonly string[]): Record<string, string | null> {
  const ws = useWorkspace();
  const sync = useSyncStatus(ws.engine);
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  const wanted = ids.join(',');

  // biome-ignore lint/correctness/useExhaustiveDependencies: `wanted` stands for `ids`; a finished sync is the cue to look again
  useEffect(() => {
    let alive = true;
    const made: string[] = [];
    void (async () => {
      const next: Record<string, string | null> = {};
      for (const id of ids) {
        const img = await loadImage(ws.db, id).catch(() => null);
        if (img) {
          const url = URL.createObjectURL(new Blob([img.bytes as BlobPart], { type: img.mime }));
          made.push(url);
          next[id] = url;
        } else next[id] = null;
      }
      if (alive) setUrls(next);
      else for (const u of made) URL.revokeObjectURL(u);
    })();
    return () => {
      alive = false;
      // The addresses are released a moment later, once the screen has stopped using them.
      setTimeout(() => {
        for (const u of made) URL.revokeObjectURL(u);
      }, 1000);
    };
  }, [ws.db, wanted, sync.lastSyncedAt]);

  return urls;
}
